import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/common/Card/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getDeadlineWatch } from '../../services/competitionWorkflowService';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/helpers';
import styles from './CompetitionDeadlineWatch.module.css';

/**
 * Components a student has handed in that nobody has approved yet, ordered by
 * how close the deadline is.
 *
 * Deliberately not the same thing as the Competition Review queue: that lists
 * matcher recommendation runs awaiting a POC decision. This lists submitted
 * work awaiting approval — the thing a missed deadline actually costs.
 *
 * Overdue rows are shown rather than hidden. Past its deadline and still
 * unreviewed is the most urgent state, not a closed one.
 */
const WINDOWS = [
  { label: 'Overdue only', days: 0 },
  { label: 'Next 3 days', days: 3 },
  { label: 'Next 7 days', days: 7 },
  { label: 'Next 14 days', days: 14 },
  { label: 'Next 30 days', days: 30 },
];

const CompetitionDeadlineWatch = () => {
  const toast = useToast();
  const [withinDays, setWithinDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (days) => {
    setLoading(true);
    try {
      setData(await getDeadlineWatch(days));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not load the deadline watch.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(withinDays); }, [withinDays, load]);

  const rows = data?.rows || [];

  const urgency = (r) => {
    if (r.overdue) return styles.overdue;
    if (r.days_left <= 2) return styles.urgent;
    return '';
  };

  const dueLabel = (r) => {
    if (r.days_left < 0) return `${Math.abs(r.days_left)}d overdue`;
    if (r.days_left === 0) return 'due today';
    if (r.days_left === 1) return 'due tomorrow';
    return `in ${r.days_left}d`;
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Approaching Deadlines</h1>
          <p className={styles.subtitle}>
            Submitted competition work that has not been approved yet.
          </p>
        </div>
        <select
          className={styles.select}
          value={withinDays}
          onChange={(e) => setWithinDays(Number(e.target.value))}
        >
          {WINDOWS.map((w) => (
            <option key={w.days} value={w.days}>{w.label}</option>
          ))}
        </select>
      </div>

      {data && (
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{data.total}</span>
            <span className={styles.statLabel}>awaiting approval</span>
          </div>
          <div className={`${styles.stat} ${data.overdue ? styles.statAlert : ''}`}>
            <span className={styles.statValue}>{data.overdue}</span>
            <span className={styles.statLabel}>already overdue</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {new Set(rows.map((r) => r.student_id)).size}
            </span>
            <span className={styles.statLabel}>students</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <Card>
          <p className={styles.empty}>
            Nothing is waiting on a reviewer in this window.
          </p>
        </Card>
      ) : (
        <Card>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Competition</th>
                  <th>Component</th>
                  <th>Status</th>
                  <th>Deadline</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.submission_id} className={urgency(r)}>
                    <td>
                      <div className={styles.name}>{r.student_name || '—'}</div>
                      {r.school && <div className={styles.sub}>{r.school}</div>}
                    </td>
                    <td>{r.competition || '—'}</td>
                    <td>
                      {r.component || '—'}
                      {r.revision_count > 0 && (
                        <span className={styles.badge}>rev {r.revision_count}</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.status}>
                        {r.status === 'changes_required' ? 'changes requested' : r.status}
                      </span>
                    </td>
                    <td>{r.internal_deadline ? formatDate(r.internal_deadline) : '—'}</td>
                    <td className={styles.due}>{dueLabel(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default CompetitionDeadlineWatch;
